import {
	MAX_SHARE_FILE_BYTES,
	MAX_SHARE_FILES,
	PENDING_SHARE_TTL_MS,
} from './constants';
import type { PendingShare, PendingShareFile } from './types';

export function isPendingShareExpired(
	share: PendingShare,
	nowMs: number = Date.now(),
): boolean {
	const receivedAt = Date.parse(share.receivedAt);

	if (!Number.isFinite(receivedAt)) {
		return true;
	}

	return nowMs - receivedAt > PENDING_SHARE_TTL_MS;
}

export function isShareFileTooLarge(size: number): boolean {
	return size > MAX_SHARE_FILE_BYTES;
}

export function canSendPendingShare(share: PendingShare): boolean {
	const hasSendableFile = share.files.some((file) => !isShareFileTooLarge(file.size));
	const hasText = Boolean(share.sharedText?.trim());

	return hasSendableFile || (hasText && share.files.length === 0);
}

export function selectSendableFiles(files: PendingShareFile[]): PendingShareFile[] {
	return files.filter((file) => !isShareFileTooLarge(file.size)).slice(0, MAX_SHARE_FILES);
}

export function sanitizeShareFileName(name: string): string {
	const trimmed = name.trim() || 'shared-file';
	const withoutPath = trimmed.replace(/^.*[/\\]/, '');

	return withoutPath.replace(/[^\w.\-()+ ]+/g, '_').slice(0, 180) || 'shared-file';
}

export function parsePendingShareJson(raw: string | null | undefined): PendingShare | null {
	if (!raw) {
		return null;
	}

	try {
		const parsed = JSON.parse(raw) as PendingShare;

		if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.files)) {
			return null;
		}

		if (typeof parsed.receivedAt !== 'string') {
			return null;
		}

		return {
			files: parsed.files.filter(
				(file): file is PendingShareFile =>
					Boolean(file) &&
					typeof file.name === 'string' &&
					typeof file.mimeType === 'string' &&
					typeof file.localPath === 'string' &&
					typeof file.size === 'number',
			),
			sharedText: typeof parsed.sharedText === 'string' ? parsed.sharedText : undefined,
			title: typeof parsed.title === 'string' ? parsed.title : undefined,
			receivedAt: parsed.receivedAt,
			truncated: parsed.truncated === true,
		};
	} catch {
		return null;
	}
}
